/**
 * AiToolsPanel — tabbed right-side panel: **Assistant** (existing chat) and
 * **Actions** (AI-powered graph actions, starting with "Connect Disconnected
 * Nodes"). The Bot toolbar button continues to toggle the whole panel; the
 * tab selection itself is local, per-session state.
 *
 * NOTE: the ontology graph canvas does not support multi-node selection
 * (ReactFlow `onSelectionChange` here only ever reports a single selected
 * node), so the "Connect Disconnected Nodes" action cannot rely on canvas
 * selection to target multiple isolated nodes. Instead it exposes its own
 * checklist of every isolated node in the graph, with a "Select all
 * isolated nodes" convenience action.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { Tabs, TabList, Tab, TabPanels, TabPanel, Button, Slider, Checkbox } from '@carbon/react';
import { Bot, Flash } from '@carbon/icons-react';
import { ChatInterface } from './ChatInterface';
import { ConnectNodesModal } from '../ontology/ConnectNodesModal';
import { FindSimilarNodesModal } from '../ontology/FindSimilarNodesModal';
import { getMainComponentIds } from '@/lib/graph-utils';
import type { ConnectedNodeSuggestion } from '@shared/types/ontology';
import type { AddNodeProposal, AddRelationProposal, EditNodeProposal } from '@snxt/snxt-onto-share';

interface AiToolsPanelProps {
  ontology: {
    id?: string;
    nodes: Node[];
    edges: Edge[];
    domain?: string;
  };
  contextData?: {
    nodes: Node[];
    edges: Edge[];
  };
  onCitationClick?: (nodeId: string) => void;
  selectedNodes: Node[];
  allNodes: Node[];
  allEdges: Edge[];
  userId: string;
  onAddEdges: (suggestions: ConnectedNodeSuggestion[]) => void;
  /** Called when the user accepts removal of one or more similar/duplicate nodes. */
  onDeleteNodes?: (nodeIds: string[]) => void;
  /** Called when the Co-Ontologist proposes adding a node and the user accepts. */
  onAddNode?: (proposal: AddNodeProposal) => void;
  /** Called when the Co-Ontologist proposes adding a relation and the user accepts. */
  onAddEdge?: (proposal: AddRelationProposal) => void;
  /** Called when the Co-Ontologist proposes editing a node and the user accepts. */
  onUpdateNode?: (proposal: EditNodeProposal) => void;
  /** Forwarded to ChatInterface — triggers auto-save after each accepted proposal. */
  onProposalAccepted?: () => void;
}

export function AiToolsPanel({
  ontology,
  contextData,
  onCitationClick,
  selectedNodes,
  allNodes,
  allEdges,
  userId,
  onAddEdges,
  onAddNode,
  onAddEdge,
  onUpdateNode,
  onProposalAccepted,
  onDeleteNodes,
}: AiToolsPanelProps) {
  const [maxSuggestionsPerNode, setMaxSuggestionsPerNode] = useState(3);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isSimilarNodesModalOpen, setIsSimilarNodesModalOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const mainComponentIds = useMemo(
    () => getMainComponentIds(allNodes, allEdges),
    [allNodes, allEdges],
  );

  // Every isolated node in the graph — the canvas does not support
  // multi-selection, so this checklist is the only way to target more than
  // one isolated node for the AI action.
  const isolatedNodes = useMemo(
    () => allNodes.filter((n) => !mainComponentIds.has(n.id)),
    [allNodes, mainComponentIds],
  );

  // The canvas can still report a single selected node — seed the checklist
  // with it when it's an isolated one, without clearing prior checkbox picks.
  useEffect(() => {
    const isolatedSelected = selectedNodes.filter((n) => !mainComponentIds.has(n.id));
    if (isolatedSelected.length === 0) return;
    setCheckedIds((prev) => {
      const next = new Set(prev);
      isolatedSelected.forEach((n) => next.add(n.id));
      return next;
    });
  }, [selectedNodes, mainComponentIds]);

  const checkedNodes = useMemo(
    () => isolatedNodes.filter((n) => checkedIds.has(n.id)),
    [isolatedNodes, checkedIds],
  );

  const canConnect = checkedNodes.length > 0;

  const toggleChecked = (nodeId: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const selectAllIsolated = () => {
    setCheckedIds(new Set(isolatedNodes.map((n) => n.id)));
  };

  const clearSelection = () => {
    setCheckedIds(new Set());
  };

  return (
    <>
      <Tabs>
        <TabList aria-label="AI tools tabs" contained>
          <Tab renderIcon={Bot}>Assistant</Tab>
          <Tab renderIcon={Flash}>Actions</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <ChatInterface
              ontology={ontology}
              contextData={contextData}
              onCitationClick={onCitationClick}
              onAddNode={onAddNode}
              onAddEdge={onAddEdge}
              onUpdateNode={onUpdateNode}
              onProposalAccepted={onProposalAccepted}
            />
          </TabPanel>
          <TabPanel>
            <div className="ai-actions-panel">

              {/* ── Action 1: Connect Disconnected Nodes ── */}
              <div className="ai-actions-panel__action">
                <section className="ai-actions-panel__section">
                  <h5 className="ai-actions-panel__heading">Connect Disconnected Nodes</h5>
                  <p className="ai-actions-panel__description">
                    Pick the isolated nodes to connect, and let the AI propose relationships
                    linking them to the rest of the graph.
                  </p>
                  <p className="ai-actions-panel__note">
                    Note: the graph canvas does not support multi-node selection — use the
                    checklist below instead.
                  </p>
                </section>

                {isolatedNodes.length === 0 && (
                  <p className="ai-actions-panel__empty">
                    There are no isolated nodes in this graph.
                  </p>
                )}

                {isolatedNodes.length > 0 && (
                  <>
                    <div className="ai-actions-panel__toolbar">
                      <Button kind="tertiary" size="sm" onClick={selectAllIsolated}>
                        Select all isolated nodes ({isolatedNodes.length})
                      </Button>
                      <Button kind="ghost" size="sm" onClick={clearSelection} disabled={checkedIds.size === 0}>
                        Clear
                      </Button>
                    </div>

                    <div className="ai-actions-panel__checklist">
                      {isolatedNodes.map((n) => (
                        <Checkbox
                          key={n.id}
                          id={`connect-nodes-isolated-${n.id}`}
                          labelText={(n.data?.label as string) || n.id}
                          checked={checkedIds.has(n.id)}
                          onChange={() => toggleChecked(n.id)}
                        />
                      ))}
                    </div>
                  </>
                )}

                <div className="ai-actions-panel__slider">
                  <Slider
                    id="connect-nodes-max-suggestions"
                    labelText="Suggestions per node"
                    min={1}
                    max={5}
                    step={1}
                    value={maxSuggestionsPerNode}
                    onChange={({ value }: { value: number }) => setMaxSuggestionsPerNode(value)}
                    disabled={!canConnect}
                  />
                </div>

                <Button disabled={!canConnect} onClick={() => setIsConnectModalOpen(true)}>
                  Connect with AI
                </Button>
              </div>

              <hr className="ai-actions-panel__divider" />

              {/* ── Action 2: Identify Semantically Similar Nodes ── */}
              <div className="ai-actions-panel__action">
                <section className="ai-actions-panel__section">
                  <h5 className="ai-actions-panel__heading">Identify Semantically Similar Nodes</h5>
                  <p className="ai-actions-panel__description">
                    Let the AI scan the graph for duplicate or near-duplicate concepts, then choose
                    which ones to remove.
                  </p>
                </section>

                <Button
                  disabled={allNodes.length < 2}
                  onClick={() => setIsSimilarNodesModalOpen(true)}
                >
                  Find similar nodes
                </Button>
              </div>

            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <ConnectNodesModal
        isOpen={isConnectModalOpen}
        selectedNodes={checkedNodes}
        allNodes={allNodes}
        allEdges={allEdges}
        maxSuggestionsPerNode={maxSuggestionsPerNode}
        userId={userId}
        onAccept={onAddEdges}
        onClose={() => setIsConnectModalOpen(false)}
      />

      <FindSimilarNodesModal
        isOpen={isSimilarNodesModalOpen}
        allNodes={allNodes}
        onRemoveNodes={(ids) => onDeleteNodes?.(ids)}
        onClose={() => setIsSimilarNodesModalOpen(false)}
      />
    </>
  );
}

// Made with Bob
